import React from "react";

import { useParams, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import {
  useRegenerateInvoiceMutation,
  useGetInvoiceDetailsQuery,
  
} from "../../slices/billingApiSlice";

import {
  
  useGetBillingSettingsQuery,
} from "../../slices/coachingCenterBillingApiSlice";

const InvoiceRegenerateScreen = () => {
  const { invoiceId } = useParams();

  const navigate = useNavigate();

  // =====================================================
  // Get Invoice
  // =====================================================

  const {
    data,
    isLoading: invoiceLoading,
    error: invoiceError,
  } = useGetInvoiceDetailsQuery(invoiceId);

  const invoice = data?.data || data;

  // =====================================================
  // Coaching Center ID
  // =====================================================

  const coachingCenterId =
    invoice?.coachingCenter?._id ||
    invoice?.coachingCenter?.id ||
    invoice?.coachingCenter ||
    invoice?.coachingCenterId;

  // =====================================================
  // Get CURRENT Billing Settings
  // =====================================================
  //
  // IMPORTANT:
  //
  // This is intentionally separate from invoice.discount.
  //
  // invoice.discount
  //      = old snapshot stored on invoice
  //
  // billingSettings
  //      = current document from
  //        CoachingCenterBillingSettings
  //
  // =====================================================

  const {
    data: billingSettingsResponse,
    isLoading: billingSettingsLoading,
    error: billingSettingsError,
  } = useGetBillingSettingsQuery(coachingCenterId, {
    skip: !coachingCenterId,
  });

  const billingSettings =
    billingSettingsResponse?.data?.settings ||
    billingSettingsResponse?.settings ||
    billingSettingsResponse?.data ||
    null;

  const currentDiscount =
    billingSettings?.discount || null;

  // =====================================================
  // Debug Current Billing Settings
  // =====================================================

  console.log(
    "========== INVOICE REGENERATE DEBUG ==========",
  );

  console.log(
    "Invoice ID:",
    invoiceId,
  );

  console.log(
    "Invoice:",
    invoice,
  );

  console.log(
    "Coaching Center ID:",
    coachingCenterId,
  );

  console.log(
    "Billing Settings Response:",
    billingSettingsResponse,
  );

  console.log(
    "Current Billing Settings:",
    billingSettings,
  );

  console.log(
    "Current Discount:",
    currentDiscount,
  );

  console.log(
    "Billing Settings Loading:",
    billingSettingsLoading,
  );

  console.log(
    "Billing Settings Error:",
    billingSettingsError,
  );

  // =====================================================
  // Regenerate Mutation
  // =====================================================

  const [
    regenerateInvoice,
    { isLoading: regenerateLoading },
  ] = useRegenerateInvoiceMutation();

  // =====================================================
  // Helpers
  // =====================================================

  const formatAmount = (amount) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-IN");
  };

  // =====================================================
  // Format Current Discount
  // =====================================================

  const formatConfiguredDiscount = (discount) => {
    if (!discount) {
      return "No discount configuration";
    }

    if (discount.enabled !== true) {
      return "Not enabled";
    }

    if (discount.type === "percentage") {
      return `${Number(discount.value || 0)}%`;
    }

    if (discount.type === "fixed") {
      return formatAmount(discount.value);
    }

    return "Configured";
  };

  // =====================================================
  // Current Discount Status
  // =====================================================

  const getCurrentDiscountStatus = (discount) => {
    if (!discount) {
      return "No configuration";
    }

    if (billingSettings?.isActive !== true) {
      return "Billing settings inactive";
    }

    if (discount.enabled !== true) {
      return "Not enabled";
    }

    return "Enabled";
  };

  // =====================================================
  // Check Discount Validity
  // =====================================================
  //
  // This is only for displaying the CURRENT configuration.
  //
  // Actual calculation remains the responsibility of
  // calculateBilling().
  //
  // =====================================================

  const getDiscountValidity = (discount) => {
    if (!discount || discount.enabled !== true) {
      return null;
    }

    const now = new Date();

    const validFrom = discount.validFrom
      ? new Date(discount.validFrom)
      : null;

    const validUntil = discount.validUntil
      ? new Date(discount.validUntil)
      : null;

    if (
      validFrom &&
      !Number.isNaN(validFrom.getTime()) &&
      now < validFrom
    ) {
      return "Not started";
    }

    if (
      validUntil &&
      !Number.isNaN(validUntil.getTime()) &&
      now > validUntil
    ) {
      return "Expired";
    }

    return "Currently valid";
  };

  const discountValidity =
    getDiscountValidity(currentDiscount);

  // =====================================================
  // Status Style
  // =====================================================

  const getStatusStyle = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";

      case "cancelled":
        return "bg-red-100 text-red-700";

      case "overdue":
        return "bg-red-100 text-red-700";

      case "pending":
      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  // =====================================================
  // Regenerate Handler
  // =====================================================

  const regenerateHandler = async () => {
    if (!invoiceId) {
      toast.error("Invoice ID is required");
      return;
    }

    if (invoice?.status === "paid") {
      toast.error("Paid invoice cannot be regenerated");
      return;
    }

    if (invoice?.status === "cancelled") {
      toast.error("Cancelled invoice cannot be regenerated");
      return;
    }

    if (invoice?.status !== "pending") {
      toast.error("Only pending invoices can be regenerated");
      return;
    }

    // ---------------------------------------------------
    // Make sure current settings have finished loading.
    // ---------------------------------------------------

    if (billingSettingsLoading) {
      toast.info(
        "Current billing settings are still loading. Please wait.",
      );

      return;
    }

    // ---------------------------------------------------
    // Warn if current settings could not be loaded.
    // ---------------------------------------------------

    if (billingSettingsError) {
      toast.error(
        "Unable to load current billing settings. Invoice was not regenerated.",
      );

      return;
    }

    console.log(
      "========== REGENERATING INVOICE ==========",
    );

    console.log(
      "Invoice ID:",
      invoiceId,
    );

    console.log(
      "Current Billing Settings:",
      billingSettings,
    );

    console.log(
      "Current Discount:",
      currentDiscount,
    );

    try {
      const response =
        await regenerateInvoice(invoiceId).unwrap();

      const regeneratedInvoice =
        response?.data || response;

      console.log(
        "Regenerated Invoice:",
        regeneratedInvoice,
      );

      toast.success(
        regeneratedInvoice?.invoiceNumber
          ? `Invoice ${regeneratedInvoice.invoiceNumber} regenerated successfully`
          : "Invoice regenerated successfully",
      );

      navigate(
        `/billing/invoices/${invoiceId}`,
      );
    } catch (error) {
      console.error(
        "Invoice regeneration failed:",
        error,
      );

      toast.error(
        error?.data?.message ||
          error?.error ||
          "Invoice regeneration failed",
      );
    }
  };

  // =====================================================
  // Loading State
  // =====================================================

  if (invoiceLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-gray-500">
          Loading invoice...
        </div>
      </div>
    );
  }

  // =====================================================
  // Error State
  // =====================================================

  if (invoiceError || !invoice) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-5">
          <p className="font-semibold">
            Unable to load invoice
          </p>

          <p className="text-sm mt-1">
            {invoiceError?.data?.message ||
              invoiceError?.error ||
              "Invoice not found"}
          </p>
        </div>
      </div>
    );
  }

  // =====================================================
  // Current Invoice Values
  // =====================================================

  const coachingCenterName =
    invoice.coachingCenter?.name ||
    invoice.coachingCenterName ||
    "-";

  const calculatedSubtotal = Number(
    invoice.calculatedSubtotal ??
      invoice.subtotal ??
      0,
  );

  const discountAmount = Number(
    invoice.discountAmount || 0,
  );

  const discountedAmount = Number(
    invoice.discountedAmount ??
      Math.max(
        calculatedSubtotal - discountAmount,
        0,
      ),
  );

  const taxableAmount = Number(
    invoice.taxableAmount ??
      invoice.subtotal ??
      0,
  );

  const totalTaxAmount = Number(
    invoice.totalTaxAmount ??
      invoice.gst?.amount ??
      0,
  );

  const total = Number(
    invoice.total || 0,
  );

  const paidAmount = Number(
    invoice.paidAmount || 0,
  );

  const balanceAmount = Math.max(
    total - paidAmount,
    0,
  );

  const minimumInvoiceApplied =
    invoice.minimumInvoiceApplied === true;

  // -----------------------------------------------------
  // This is the OLD invoice snapshot.
  // -----------------------------------------------------

  const invoiceDiscountSnapshot =
    invoice.discount || null;

  const canRegenerate =
    invoice.status === "pending";

  // =====================================================
  // Render
  // =====================================================

  return (
    <div>
      {/* =====================================================
          Header
      ===================================================== */}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Regenerate Invoice
        </h1>

        <p className="text-gray-500 mt-1">
          Recalculate this invoice using the latest billing
          usage, pricing, and coaching-center billing settings.
        </p>
      </div>

      {/* =====================================================
          Billing Settings Notice
      ===================================================== */}

      <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-5 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-700 font-bold shrink-0">
            i
          </div>

          <div>
            <p className="font-semibold">
              Current billing settings are loaded
            </p>

            <p className="text-sm mt-1">
              The discount shown below is the current
              coaching-center billing configuration loaded
              from the database.
            </p>

            <p className="text-sm mt-2">
              Clicking "Regenerate Invoice" will recalculate
              the invoice using the current settings.
            </p>
          </div>
        </div>
      </div>

      {/* =====================================================
          Invoice Details
      ===================================================== */}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <div className="bg-gray-50 px-5 py-3 font-semibold text-gray-700 border-b">
          Invoice Details
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <p className="text-sm text-gray-500">
                Invoice Number
              </p>

              <p className="font-semibold text-gray-800">
                {invoice.invoiceNumber || "-"}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">
                Coaching Center
              </p>

              <p className="font-semibold text-gray-800">
                {coachingCenterName}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">
                Invoice Date
              </p>

              <p className="text-gray-800">
                {formatDate(invoice.invoiceDate)}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">
                Billing Period
              </p>

              <p className="text-gray-800">
                {formatDate(
                  invoice.billingPeriodStart,
                )}
                {" - "}
                {formatDate(
                  invoice.billingPeriodEnd,
                )}
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <p className="text-sm text-gray-500">
                Current Status
              </p>

              <span
                className={`
                  inline-block
                  mt-1
                  px-3
                  py-1
                  rounded-full
                  text-xs
                  font-semibold
                  ${getStatusStyle(invoice.status)}
                `}
              >
                {invoice.status}
              </span>
            </div>

            <div>
              <p className="text-sm text-gray-500">
                Current Subtotal
              </p>

              <p className="font-semibold text-gray-800">
                {formatAmount(calculatedSubtotal)}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">
                Current Discount
              </p>

              <p className="font-semibold text-gray-800">
                {formatAmount(discountAmount)}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">
                Taxable Amount
              </p>

              <p className="font-semibold text-gray-800">
                {formatAmount(taxableAmount)}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">
                Current Total
              </p>

              <p className="text-xl font-bold text-blue-600">
                {formatAmount(total)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          CURRENT BILLING DISCOUNT
      ===================================================== */}

      <div className="bg-white border border-blue-200 rounded-lg shadow-sm mb-6">
        <div className="bg-blue-50 px-5 py-3 font-semibold text-blue-800 border-b border-blue-200">
          Current Discount Configuration
        </div>

        {billingSettingsLoading ? (
          <div className="p-5">
            <div className="flex items-center gap-3 text-gray-500">
              <span
                className="
                  animate-spin
                  h-5
                  w-5
                  border-2
                  border-blue-600
                  border-t-transparent
                  rounded-full
                "
              />

              Loading current billing settings...
            </div>
          </div>
        ) : billingSettingsError ? (
          <div className="p-5">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              <p className="font-semibold">
                Unable to load current billing settings
              </p>

              <p className="text-sm mt-1">
                {billingSettingsError?.data?.message ||
                  billingSettingsError?.error ||
                  "Billing settings request failed"}
              </p>
            </div>
          </div>
        ) : !billingSettings ? (
          <div className="p-5">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
              <p className="font-semibold">
                No billing settings found
              </p>

              <p className="text-sm mt-1">
                No current coaching-center billing settings
                were returned by the server.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Status */}

              <div>
                <p className="text-sm text-gray-500">
                  Discount Status
                </p>

                <p
                  className={`
                    font-semibold
                    mt-1
                    ${
                      currentDiscount?.enabled === true &&
                      billingSettings?.isActive === true
                        ? "text-green-700"
                        : "text-gray-800"
                    }
                  `}
                >
                  {getCurrentDiscountStatus(
                    currentDiscount,
                  )}
                </p>
              </div>

              {/* Type */}

              <div>
                <p className="text-sm text-gray-500">
                  Discount Type
                </p>

                <p className="font-semibold text-gray-800 mt-1 capitalize">
                  {currentDiscount?.type || "-"}
                </p>
              </div>

              {/* Value */}

              <div>
                <p className="text-sm text-gray-500">
                  Configured Value
                </p>

                <p className="font-semibold text-gray-800 mt-1">
                  {formatConfiguredDiscount(
                    currentDiscount,
                  )}
                </p>
              </div>

              {/* Maximum */}

              <div>
                <p className="text-sm text-gray-500">
                  Maximum Discount
                </p>

                <p className="font-semibold text-gray-800 mt-1">
                  {currentDiscount?.maxAmount !==
                    null &&
                  currentDiscount?.maxAmount !==
                    undefined
                    ? formatAmount(
                        currentDiscount.maxAmount,
                      )
                    : "No limit"}
                </p>
              </div>

              {/* Reason */}

              <div>
                <p className="text-sm text-gray-500">
                  Reason
                </p>

                <p className="font-semibold text-gray-800 mt-1">
                  {currentDiscount?.reason || "-"}
                </p>
              </div>

              {/* Validity */}

              <div>
                <p className="text-sm text-gray-500">
                  Current Validity
                </p>

                <p
                  className={`
                    font-semibold
                    mt-1
                    ${
                      discountValidity ===
                      "Currently valid"
                        ? "text-green-700"
                        : "text-yellow-700"
                    }
                  `}
                >
                  {discountValidity || "-"}
                </p>
              </div>

              {/* Valid From */}

              <div>
                <p className="text-sm text-gray-500">
                  Valid From
                </p>

                <p className="text-gray-800 mt-1">
                  {formatDate(
                    currentDiscount?.validFrom,
                  )}
                </p>
              </div>

              {/* Valid Until */}

              <div>
                <p className="text-sm text-gray-500">
                  Valid Until
                </p>

                <p className="text-gray-800 mt-1">
                  {formatDate(
                    currentDiscount?.validUntil,
                  )}
                </p>
              </div>

              {/* Settings Updated */}

              <div>
                <p className="text-sm text-gray-500">
                  Settings Updated
                </p>

                <p className="text-gray-800 mt-1">
                  {formatDate(
                    billingSettings?.updatedAt,
                  )}
                </p>
              </div>
            </div>

            <div className="mx-5 mb-5 rounded-lg bg-blue-50 border border-blue-100 p-4">
              <p className="text-sm text-blue-800">
                <strong>Source:</strong> Current
                CoachingCenterBillingSettings document.
              </p>

              <p className="text-sm text-blue-700 mt-1">
                This is the discount configuration that will
                be used when the invoice is regenerated.
              </p>
            </div>
          </>
        )}
      </div>

      {/* =====================================================
          PREVIOUS INVOICE DISCOUNT SNAPSHOT
      ===================================================== */}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <div className="bg-gray-50 px-5 py-3 font-semibold text-gray-700 border-b">
          Previous Invoice Discount Snapshot
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div>
            <p className="text-sm text-gray-500">
              Status
            </p>

            <p className="font-semibold text-gray-800 mt-1">
              {!invoiceDiscountSnapshot?.enabled
                ? "Not enabled"
                : invoiceDiscountSnapshot?.applied
                  ? "Applied"
                  : "Not applied"}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-500">
              Type
            </p>

            <p className="font-semibold text-gray-800 mt-1 capitalize">
              {invoiceDiscountSnapshot?.type ||
                "-"}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-500">
              Configured Value
            </p>

            <p className="font-semibold text-gray-800 mt-1">
              {formatConfiguredDiscount(
                invoiceDiscountSnapshot,
              )}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-500">
              Applied Amount
            </p>

            <p className="font-semibold text-green-700 mt-1">
              {formatAmount(
                invoiceDiscountSnapshot?.amount ??
                  discountAmount,
              )}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-500">
              Reason
            </p>

            <p className="font-semibold text-gray-800 mt-1">
              {invoiceDiscountSnapshot?.reason ||
                "-"}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-500">
              Valid From
            </p>

            <p className="text-gray-800 mt-1">
              {formatDate(
                invoiceDiscountSnapshot?.validFrom,
              )}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-500">
              Valid Until
            </p>

            <p className="text-gray-800 mt-1">
              {formatDate(
                invoiceDiscountSnapshot?.validUntil,
              )}
            </p>
          </div>
        </div>

        <div className="mx-5 mb-5 rounded-lg bg-gray-50 border border-gray-200 p-4">
          <p className="text-sm text-gray-600">
            This is the discount snapshot currently stored
            on the invoice. It is not the current billing
            configuration.
          </p>
        </div>
      </div>

      {/* =====================================================
          Current Usage Snapshot
      ===================================================== */}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <div className="bg-gray-50 px-5 py-3 font-semibold text-gray-700 border-b">
          Current Usage Snapshot
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-lg bg-blue-50 border border-blue-100 p-4">
            <p className="text-sm text-blue-700">
              Center Admins
            </p>

            <p className="text-2xl font-bold text-blue-800 mt-1">
              {invoice.adminCount || 0}
            </p>
          </div>

          <div className="rounded-lg bg-purple-50 border border-purple-100 p-4">
            <p className="text-sm text-purple-700">
              Active Students
            </p>

            <p className="text-2xl font-bold text-purple-800 mt-1">
              {invoice.activeStudentCount || 0}
            </p>
          </div>
        </div>
      </div>

      {/* =====================================================
          Calculation Summary
      ===================================================== */}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <div className="bg-gray-50 px-5 py-3 font-semibold text-gray-700 border-b">
          Current Calculation
        </div>

        <div className="p-5 space-y-3">
          <div className="flex justify-between gap-4">
            <span className="text-gray-600">
              Calculated Subtotal
            </span>

            <span className="font-semibold text-gray-800">
              {formatAmount(calculatedSubtotal)}
            </span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-gray-600">
              Discount
            </span>

            <span className="font-semibold text-green-600">
              - {formatAmount(discountAmount)}
            </span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-gray-600">
              After Discount
            </span>

            <span className="font-semibold text-gray-800">
              {formatAmount(discountedAmount)}
            </span>
          </div>

          {minimumInvoiceApplied && (
            <div className="flex justify-between gap-4 rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3">
              <div>
                <p className="font-semibold text-yellow-800">
                  Minimum Invoice Applied
                </p>

                <p className="text-xs text-yellow-700 mt-1">
                  The minimum invoice floor is protected
                  from discounts.
                </p>
              </div>

              <span className="font-semibold text-yellow-800">
                {formatAmount(taxableAmount)}
              </span>
            </div>
          )}

          <div className="flex justify-between gap-4">
            <span className="text-gray-600">
              GST
            </span>

            <span className="font-semibold text-gray-800">
              {formatAmount(totalTaxAmount)}
            </span>
          </div>

          <div className="border-t pt-4 mt-4 flex justify-between gap-4">
            <span className="font-bold text-gray-800">
              Current Total
            </span>

            <span className="text-xl font-bold text-blue-600">
              {formatAmount(total)}
            </span>
          </div>
        </div>
      </div>

      {/* =====================================================
          Regeneration Warning
      ===================================================== */}

      <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-lg p-5 mb-6">
        <p className="font-semibold">
          Regenerating this invoice will recalculate:
        </p>

        <ul className="list-disc ml-6 mt-3 space-y-1">
          <li>Current active center admins</li>
          <li>Current active students</li>
          <li>Student usage charges</li>
          <li>Center admin charges</li>
          <li>Current billing plan pricing</li>
          <li>Current coaching-center discount settings</li>
          <li>Invoice line items</li>
          <li>Minimum invoice rule</li>
          <li>Applicable GST</li>
          <li>Final invoice total</li>
        </ul>

        <p className="mt-4 text-sm">
          The current billing configuration displayed above
          will be used by the server during regeneration.
        </p>
      </div>

      {/* =====================================================
          Existing Payment Warning
      ===================================================== */}

      {paidAmount > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-5 mb-6">
          <p className="font-semibold">
            Payment already recorded
          </p>

          <p className="text-sm mt-1">
            This invoice has a recorded payment of{" "}
            <strong>
              {formatAmount(paidAmount)}
            </strong>
            .
          </p>

          <p className="text-sm mt-1">
            Balance:{" "}
            <strong>
              {formatAmount(balanceAmount)}
            </strong>
          </p>
        </div>
      )}

      {/* =====================================================
          Cannot Regenerate
      ===================================================== */}

      {!canRegenerate && (
        <div className="bg-gray-100 border border-gray-200 text-gray-700 rounded-lg p-5 mb-6">
          <p className="font-semibold">
            This invoice cannot be regenerated.
          </p>

          <p className="text-sm mt-1">
            Only pending invoices can be regenerated.
            This invoice is currently{" "}
            <strong>{invoice.status}</strong>.
          </p>
        </div>
      )}

      {/* =====================================================
          Actions
      ===================================================== */}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          disabled={regenerateLoading}
          onClick={() => navigate(-1)}
          className="
            px-5
            py-2.5
            rounded-lg
            bg-gray-500
            hover:bg-gray-600
            text-white
            font-medium
            disabled:opacity-50
          "
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={
            regenerateLoading ||
            billingSettingsLoading ||
            !canRegenerate
          }
          onClick={regenerateHandler}
          className="
            px-5
            py-2.5
            rounded-lg
            bg-blue-600
            hover:bg-blue-700
            text-white
            font-medium
            flex
            items-center
            gap-2
            disabled:opacity-50
            disabled:cursor-not-allowed
          "
        >
          {regenerateLoading ? (
            <>
              <span
                className="
                  animate-spin
                  h-4
                  w-4
                  border-2
                  border-white
                  border-t-transparent
                  rounded-full
                "
              />

              Regenerating...
            </>
          ) : billingSettingsLoading ? (
            "Loading Billing Settings..."
          ) : (
            "Regenerate Invoice"
          )}
        </button>
      </div>
    </div>
  );
};

export default InvoiceRegenerateScreen;